import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, AlertTriangle, XCircle, Shield } from "lucide-react";
import { useToolLifecycle } from "@/utils/trackingHooks";
import LeadForm from "@/forms/engine/LeadForm";
import toolHiddenFees from "@/forms/definitions/toolHiddenFees";

const HF_ITEM_NAME = "hidden_fees_checker";
const HF_ITEM_OPTS = { itemType: "calculator" };

const CLARITY_OPTIONS = [
  { value: "yes", label: "Yes, I understand it clearly" },
  { value: "somewhat", label: "Somewhat" },
  { value: "no", label: "No, not really" },
];
const CONTRACT_CLARITY_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "not_sure", label: "Not sure" },
];

const RESULT_BULLETS = {
  looks_reasonable: [
    "Your recurring fees appear broadly in line with common benchmarks",
    "A statement review may still reveal hidden structure or opportunities to simplify charges",
    "It is worth checking support, flexibility, and contract position too",
  ],
  some_fees_worth_checking: [
    "Some of your monthly charges appear above common benchmark levels",
    "A statement review could show whether those costs are justified or avoidable",
    "Clearer pricing structure may help reduce ongoing costs",
  ],
  strong_signs_of_hidden_fees: [
    "Several of your fees appear higher than common benchmark levels",
    "You may be paying more than expected in recurring monthly charges",
    "A full statement review could help identify the real opportunity to reduce costs",
  ],
};

function formatGBP(val) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(val);
}

function scorePci(v) { if (v <= 6) return 0; if (v <= 12) return 8; return 15; }
function scoreTerminal(v) { if (v <= 20) return 0; if (v <= 30) return 8; return 15; }
function scoreAuth(v) { if (v <= 0.03) return 0; if (v <= 0.05) return 8; return 15; }
function scoreService(v) { if (v <= 5) return 0; if (v <= 15) return 6; return 12; }
function scoreOther(v) { if (v <= 5) return 0; if (v <= 15) return 6; return 12; }
function scoreClarity(v) { if (v === "yes") return 0; if (v === "somewhat") return 5; return 10; }
function scoreContract(v) { return v === "yes" ? 0 : 5; }

function categoriseHiddenFees(score) {
  if (score >= 55) return { category: "strong_signs_of_hidden_fees", headline: "There are strong signs of avoidable or inflated monthly charges", description: "Based on the figures entered, several parts of your fee structure appear higher than benchmark levels and are worth reviewing in more detail." };
  if (score >= 25) return { category: "some_fees_worth_checking", headline: "Some of your monthly fees are worth checking", description: "Based on the figures entered, there are a few signs that parts of your statement may be more expensive or less transparent than they need to be." };
  return { category: "looks_reasonable", headline: "Your monthly fee structure looks fairly reasonable", description: "Based on the figures entered, your recurring fees do not immediately suggest major hidden fee issues. A full review may still uncover areas to simplify or improve." };
}

function calcFeeReviews({ pci, tr, tc, af, mtc, sf, of_ }) {
  return {
    pciReview: Math.max(0, pci - 6),
    terminalReview: Math.max(0, tr - tc * 20),
    authReview: mtc > 0 ? Math.max(0, af - 0.03) * mtc : 0,
    serviceReview: Math.max(0, sf - 5),
    otherReview: Math.max(0, of_ - 5),
  };
}

function buildFlags({ pci, trpt, af, sf, of_ }, reviews) {
  return [
    { label: "PCI fee", flagged: pci > 6, review: reviews.pciReview },
    { label: "Terminal rental", flagged: trpt > 20, review: reviews.terminalReview },
    { label: "Auth fee", flagged: af > 0.03, review: reviews.authReview },
    { label: "Service fee", flagged: sf > 5, review: reviews.serviceReview },
    { label: "Other fees", flagged: of_ > 5, review: reviews.otherReview },
  ];
}

function calculateHiddenFeesCheck(inputs) {
  const pci = parseFloat(inputs.pciFee) || 0;
  const tr = parseFloat(inputs.terminalRental) || 0;
  const tc = Math.max(parseInt(inputs.terminalCount, 10) || 0, 0);
  const af = parseFloat(inputs.authFee) || 0;
  const mtc = Math.max(parseInt(inputs.monthlyTransactionCount, 10) || 0, 0);
  const sf = parseFloat(inputs.monthlyServiceFee) || 0;
  const of_ = parseFloat(inputs.otherMonthlyFees) || 0;
  const trpt = tc > 0 ? tr / tc : tr;

  const reviews = calcFeeReviews({ pci, tr, tc, af, mtc, sf, of_ });
  const monthlyWorth = reviews.pciReview + reviews.terminalReview + reviews.authReview + reviews.serviceReview + reviews.otherReview;

  const raw = scorePci(pci) + scoreTerminal(trpt) + scoreAuth(af) + scoreService(sf) + scoreOther(of_) + scoreClarity(inputs.pricingClarity) + scoreContract(inputs.contractClarity);
  const score = Math.max(0, Math.min(100, raw));
  const flagCount = [pci > 6, trpt > 20, af > 0.03, sf > 5, of_ > 5].filter(Boolean).length;

  return { score, flagCount, flags: buildFlags({ pci, trpt, af, sf, of_ }, reviews), ...categoriseHiddenFees(score), monthlyWorth, annualWorth: monthlyWorth * 12 };
}

function NumberField({ label, name, value, onChange, placeholder, helper, testId, prefix, step = "0.01" }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1">{label}</label>
      {helper && <p className="text-xs text-brand-grey mb-1.5">{helper}</p>}
      <div className="relative">
        {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">{prefix}</span>}
        <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step={step} min="0" className={`w-full ${prefix ? "pl-8" : "pl-4"} pr-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all`} data-testid={testId} />
      </div>
    </div>
  );
}

function SelectInput({ label, name, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <select name={name} value={value} onChange={onChange} className="w-full px-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all appearance-none" data-testid={testId}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

const HIDDEN_FEES_CATEGORY_LABEL = {
  looks_reasonable: "Looks reasonable",
  some_fees_worth_checking: "Some fees worth checking",
  strong_signs_of_hidden_fees: "Strong signs of hidden fees",
};

const labelOf = (options, value) => options.find((o) => o.value === value)?.label;
const numberOrUndefined = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};
const round2 = (n) => Math.round(n * 100) / 100;
const compact = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/** Checker inputs + result as readable `context` for the Lead (see toolHiddenFees.jsx). */
function buildLeadContext(inputs, result) {
  const terminalCount = parseInt(inputs.terminalCount, 10);
  const flagged = result.flags.filter((f) => f.flagged);
  return compact({
    tool: "Hidden Fees Checker",
    score: result.score,
    category: HIDDEN_FEES_CATEGORY_LABEL[result.category] || result.category,
    flags_raised: result.flagCount,
    flagged_fees: flagged.length
      ? flagged.map((f) => `${f.label} (£${round2(f.review).toFixed(2)}/mo above benchmark)`)
      : undefined,
    monthly_worth_reviewing_gbp: round2(result.monthlyWorth),
    annual_worth_reviewing_gbp: round2(result.annualWorth),
    pci_fee_gbp_per_month: numberOrUndefined(inputs.pciFee),
    terminal_rental_gbp_per_month: numberOrUndefined(inputs.terminalRental),
    terminal_count: Number.isFinite(terminalCount) && terminalCount > 0 ? terminalCount : undefined,
    auth_fee_gbp: numberOrUndefined(inputs.authFee),
    monthly_transaction_count: numberOrUndefined(inputs.monthlyTransactionCount),
    service_fee_gbp_per_month: numberOrUndefined(inputs.monthlyServiceFee),
    other_fees_gbp_per_month: numberOrUndefined(inputs.otherMonthlyFees),
    understands_pricing: labelOf(CLARITY_OPTIONS, inputs.pricingClarity),
    knows_contract_position: labelOf(CONTRACT_CLARITY_OPTIONS, inputs.contractClarity),
  });
}

function ScoreMeter({ score, category }) {
  const color = { looks_reasonable: "text-emerald-600", some_fees_worth_checking: "text-amber-500", strong_signs_of_hidden_fees: "text-red-500" };
  const bg = { looks_reasonable: "bg-emerald-500", some_fees_worth_checking: "bg-amber-500", strong_signs_of_hidden_fees: "bg-red-500" };
  return (
    <div className="text-center result-card-animate" data-testid="hf-score-meter">
      <p className={`text-5xl font-heading font-bold ${color[category]}`} data-testid="hf-score-value">{score}</p>
      <p className="text-sm text-brand-grey mt-1">red flag score out of 100</p>
      <div className="mt-3 h-3 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${bg[category]}`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
    </div>
  );
}

function FlagSummary({ flags }) {
  return (
    <div className="space-y-1.5" data-testid="hf-flag-summary">
      {flags.map((f) => (
        <div key={f.label} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2">
            {f.flagged ? <AlertTriangle size={14} className="text-amber-500" /> : <CheckCircle size={14} className="text-emerald-500" />}
            <span className={f.flagged ? "text-brand-black font-semibold" : "text-brand-grey"}>{f.label}</span>
          </div>
          {f.flagged && <span className="text-xs font-bold text-amber-600">{`${formatGBP(f.review)}/mo above benchmark`}</span>}
        </div>
      ))}
    </div>
  );
}

function ResultDisplay({ result }) {
  const icon = { looks_reasonable: CheckCircle, some_fees_worth_checking: AlertTriangle, strong_signs_of_hidden_fees: XCircle };
  const iconColor = { looks_reasonable: "text-emerald-500", some_fees_worth_checking: "text-amber-500", strong_signs_of_hidden_fees: "text-red-500" };
  const Icon = icon[result.category];
  const bullets = RESULT_BULLETS[result.category];

  return (
    <div className="space-y-5" data-testid="hf-result">
      <ScoreMeter score={result.score} category={result.category} />

      <div className="flex items-center gap-2 justify-center">
        <Icon size={20} className={iconColor[result.category]} />
        <h4 className="font-heading font-bold text-brand-black" data-testid="hf-headline">{result.headline}</h4>
      </div>
      <p className="text-sm text-brand-grey text-center leading-relaxed">{result.description}</p>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-brand-aqua-light border border-brand-aqua/20 rounded-xl p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-1">Flags raised</p>
          <p className="text-2xl font-heading font-bold text-brand-aqua-dark" data-testid="hf-flag-count">{result.flagCount}</p>
        </div>
        <div className="bg-brand-pink-light border border-brand-pink/20 rounded-xl p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-1">Monthly to review</p>
          <p className="text-2xl font-heading font-bold text-brand-pink" data-testid="hf-monthly-review">{formatGBP(result.monthlyWorth)}</p>
        </div>
        <div className="bg-gradient-to-br from-brand-pink-light to-white border border-brand-pink/20 rounded-xl p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-1">Annual to review</p>
          <p className="text-2xl font-heading font-bold text-brand-pink" data-testid="hf-annual-review">{formatGBP(result.annualWorth)}</p>
        </div>
      </div>

      <FlagSummary flags={result.flags} />

      <div className="space-y-2 max-w-md mx-auto">
        {bullets.map((b) => (
          <div key={b} className="flex items-start gap-2.5 text-left">
            <CheckCircle size={15} className="text-brand-aqua flex-shrink-0 mt-0.5" />
            <span className="text-sm text-brand-black">{b}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Link to="/switch-and-save" className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine" data-testid="hf-cta-review">Get my statement review <ArrowRight size={16} /></Link>
        <a href="/book" className="flex-1 bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors" data-testid="hf-cta-call">Book a quick call</a>
      </div>
    </div>
  );
}

function useHiddenFeesChecker() {
  const [inputs, setInputs] = useState({
    pciFee: "", terminalRental: "", terminalCount: "1", authFee: "",
    monthlyTransactionCount: "", monthlyServiceFee: "", otherMonthlyFees: "",
    pricingClarity: "somewhat", contractClarity: "not_sure",
  });
  const [showResult, setShowResult] = useState(false);
  const { markStarted, markCompleted } = useToolLifecycle(HF_ITEM_NAME, HF_ITEM_OPTS);

  const handleChange = useCallback((e) => {
    markStarted();
    const { name, value } = e.target;
    setInputs((p) => ({ ...p, [name]: value }));
  }, [setInputs]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = useMemo(() => {
    if (!showResult) return null;
    return calculateHiddenFeesCheck(inputs);
  }, [showResult, inputs]);

  useEffect(() => { if (result) markCompleted(); }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(() => setShowResult(true), []);
  const handleReset = useCallback(() => setShowResult(false), []);

  return { inputs, handleChange, showResult, result, handleSubmit, handleReset };
}

function CheckerInputs({ inputs, handleChange }) {
  return (
    <>
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Monthly charges</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumberField label="Monthly PCI fee" name="pciFee" value={inputs.pciFee} onChange={handleChange} placeholder="e.g. 10" helper="Monthly PCI or compliance fee" prefix="£" testId="hf-pci" />
        <NumberField label="Monthly terminal rental" name="terminalRental" value={inputs.terminalRental} onChange={handleChange} placeholder="e.g. 25" helper="Total monthly terminal rental" prefix="£" testId="hf-terminal" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumberField label="Monthly service fee" name="monthlyServiceFee" value={inputs.monthlyServiceFee} onChange={handleChange} placeholder="e.g. 12" helper="Admin, platform, or service fee" prefix="£" testId="hf-service" />
        <NumberField label="Other monthly fees" name="otherMonthlyFees" value={inputs.otherMonthlyFees} onChange={handleChange} placeholder="e.g. 8" helper="Any other recurring charges" prefix="£" testId="hf-other" />
      </div>
      <div className="border-t border-brand-light-grey/50 pt-4" />
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Transaction fees</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <NumberField label="Number of terminals" name="terminalCount" value={inputs.terminalCount} onChange={handleChange} placeholder="1" step="1" testId="hf-terminal-count" />
        <NumberField label="Auth fee per transaction" name="authFee" value={inputs.authFee} onChange={handleChange} placeholder="e.g. 0.05" helper="In pounds, e.g. 0.05" prefix="£" testId="hf-auth" />
        <NumberField label="Monthly transaction count" name="monthlyTransactionCount" value={inputs.monthlyTransactionCount} onChange={handleChange} placeholder="e.g. 2000" helper="If unsure, leave as 0" step="1" testId="hf-tx-count" />
      </div>
      <div className="border-t border-brand-light-grey/50 pt-4" />
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Your understanding</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectInput label="Do you clearly understand your pricing?" name="pricingClarity" value={inputs.pricingClarity} onChange={handleChange} options={CLARITY_OPTIONS} testId="hf-clarity" />
        <SelectInput label="Do you know whether you are still in contract?" name="contractClarity" value={inputs.contractClarity} onChange={handleChange} options={CONTRACT_CLARITY_OPTIONS} testId="hf-contract" />
      </div>
    </>
  );
}

export default function HiddenFeesChecker({ className = "" }) {
  const { inputs, handleChange, showResult, result, handleSubmit, handleReset } = useHiddenFeesChecker();
  const leadContext = useMemo(() => (result ? buildLeadContext(inputs, result) : null), [inputs, result]);

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="hidden-fees-checker">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center"><Shield size={20} className="text-white" /></div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">PCI & Hidden Fees Checker</h3>
          <p className="text-xs text-brand-grey">Check whether your recurring fees look high or worth reviewing</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-5">
        {!showResult && (
          <>
            <p className="text-sm text-brand-grey leading-relaxed">Enter the recurring fees you can see on your statement and we&apos;ll show you whether any of them look high or worth checking.</p>
            <CheckerInputs inputs={inputs} handleChange={handleChange} />
            <button onClick={handleSubmit} className="w-full btn-form-ready is-ready text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2" data-testid="hf-submit">Check my fees <ArrowRight size={16} /></button>
          </>
        )}

        {showResult && result && (
          <>
            <ResultDisplay result={result} />
            <div className="border-t border-brand-light-grey/60 pt-6">
              <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Get your personalised review</h4>
              <p className="text-xs text-brand-grey text-center mb-4">Tell us where to send your tailored follow-up.</p>
              <LeadForm schema={toolHiddenFees} context={leadContext} variant="compact" />
            </div>
            <div className="text-center"><button onClick={handleReset} className="text-xs text-brand-grey hover:text-brand-aqua transition-colors underline underline-offset-2" data-testid="hf-restart">Start again</button></div>
          </>
        )}

        <p className="text-[11px] text-brand-grey leading-relaxed text-center">
          This checker provides an illustration based on the figures entered and general benchmark assumptions. It is not a formal audit or guaranteed savings calculation. A full statement review may reveal additional fees, pricing details, or contractual factors not captured here.
        </p>
      </div>
    </div>
  );
}
